import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import DataTable from '../../../components/tables/DataTable';
import Modal from '../../../components/modal';
import StatusBadge from '../../../components/StatusBadge';
import EmailTagInput from '../../../components/EmailTagInput';
import {
	listNotificationRules,
	createNotificationRule,
	updateNotificationRule,
	removeNotificationRule,
	listNotificationRuleEventTypes,
	listNotificationHistory,
} from '../../../utils/queries';
import { COMMERCE_EVENT_DESCRIPTIONS, humanizeEventType } from '../../../utils/commerceEventTypes';

const RECIPIENT_MODES = [
	{ value: 'APPROVER_FLAG', label: 'Order approvers' },
	{ value: 'ROLE_ADMIN', label: 'Store admins' },
	{ value: 'EMAIL_LIST', label: 'Specific emails' },
];

const recipientModeLabel = (value) => RECIPIENT_MODES.find((mode) => mode.value === value)?.label || value;

const emptyForm = { eventType: '', enabled: true, recipientMode: 'ROLE_ADMIN', recipientEmails: [] };

const splitEmails = (value) =>
	(value || '')
		.split(',')
		.map((entry) => entry.trim())
		.filter(Boolean);

export default function NotificationRulesTab({ storeId }) {
	const [rules, setRules] = useState([]);
	const [loading, setLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [eventTypeOptions, setEventTypeOptions] = useState([]);
	const [history, setHistory] = useState([]);

	const refresh = () => {
		if (!storeId) return;
		setLoading(true);
		listNotificationRules({ storeId }).then((result) => {
			if (result?.success) setRules(result.data.rules);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId]);

	useEffect(() => {
		listNotificationRuleEventTypes().then((result) => {
			if (result?.success) setEventTypeOptions(result.data.eventTypes);
		});
	}, []);

	useEffect(() => {
		if (!storeId) return;
		listNotificationHistory({ storeId, limit: 10 }).then((result) => {
			if (result?.success) setHistory(result.data.notifications);
		});
	}, [storeId]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setError('');
		setOpen(true);
	};

	const openEdit = (rule) => {
		setEditing(rule);
		setForm({
			eventType: rule.eventType,
			enabled: rule.enabled,
			recipientMode: rule.recipientMode,
			recipientEmails: splitEmails(rule.recipientEmails),
		});
		setError('');
		setOpen(true);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		const payload = {
			eventType: form.eventType,
			enabled: form.enabled,
			recipientMode: form.recipientMode,
			recipientEmails: form.recipientMode === 'EMAIL_LIST' ? form.recipientEmails.join(', ') : null,
		};
		const result = editing
			? await updateNotificationRule(editing.notificationRuleId, payload)
			: await createNotificationRule({ ...payload, storeId });
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		setOpen(false);
		refresh();
	};

	const handleRemove = async (id) => {
		await removeNotificationRule(id);
		refresh();
	};

	const columns = [
		{
			key: 'eventType',
			header: 'Event',
			render: (value, row) => (
				<button type='button' className='text-left' onClick={() => openEdit(row)}>
					<div className='text-black font-medium hover:underline'>{humanizeEventType(value)}</div>
					<div className='text-xs text-gray-400 font-mono'>{value}</div>
				</button>
			),
		},
		{
			key: 'recipientMode',
			header: 'Recipients',
			render: (value, row) =>
				value === 'EMAIL_LIST' ? `${recipientModeLabel(value)} (${splitEmails(row.recipientEmails).length})` : recipientModeLabel(value),
		},
		{
			key: 'enabled',
			header: 'Status',
			render: (value) => <StatusBadge status={value ? 'active' : 'inactive'} text={value ? 'Enabled' : 'Disabled'} />,
		},
		{
			key: 'notificationRuleId',
			header: '',
			sortable: false,
			render: (id) => (
				<button type='button' onClick={() => handleRemove(id)}>
					<Trash2 size={20} />
				</button>
			),
		},
	];

	const historyColumns = [
		{ key: 'eventType', header: 'Event', render: (value) => humanizeEventType(value) },
		{ key: 'recipientEmail', header: 'Recipient' },
		{
			key: 'status',
			header: 'Status',
			render: (value) => <StatusBadge status={value === 'SENT' ? 'active' : 'failed'} text={value} />,
		},
		{ key: 'sentAt', header: 'Sent', render: (value) => (value ? new Date(value).toLocaleString() : '') },
	];

	return (
		<div className='max-w-5xl'>
			<div className='flex items-center justify-between mb-6'>
				<p className='text-sm text-gray-500'>
					Configure who gets emailed when a platform event fires - order approvals, exceptions, low inventory, and more.
				</p>
				<button type='button' className='btn-primary' disabled={!storeId} onClick={openCreate}>
					New Rule
				</button>
			</div>

			<DataTable columns={columns} data={rules} loading={loading} emptyHeading='No notification rules configured yet' />

			{history.length > 0 && (
				<div className='mt-8 pt-6 border-t border-gray-200'>
					<p className='text-sm font-medium text-gray-900 mb-4'>Recent Deliveries</p>
					<DataTable columns={historyColumns} data={history} emptyHeading='No notifications sent yet' />
				</div>
			)}

			<Modal open={open} setOpen={setOpen} title={editing ? 'Edit Notification Rule' : 'New Notification Rule'}>
				<form onSubmit={handleSubmit} className='space-y-4'>
					{error && <div className='text-sm text-pi-red'>{error}</div>}
					<div>
						<label>Event</label>
						<select
							className='form-input'
							required
							value={form.eventType}
							onChange={(e) => setForm({ ...form, eventType: e.target.value })}
						>
							<option value='' disabled>
								Select an event...
							</option>
							{eventTypeOptions.map((eventType) => (
								<option key={eventType} value={eventType}>
									{humanizeEventType(eventType)}
								</option>
							))}
						</select>
						{form.eventType && (
							<p className='text-xs text-gray-400 mt-1'>{COMMERCE_EVENT_DESCRIPTIONS[form.eventType]}</p>
						)}
					</div>
					<div>
						<label>Recipients</label>
						<select
							className='form-input'
							value={form.recipientMode}
							onChange={(e) => setForm({ ...form, recipientMode: e.target.value })}
						>
							{RECIPIENT_MODES.map((mode) => (
								<option key={mode.value} value={mode.value}>
									{mode.label}
								</option>
							))}
						</select>
					</div>
					{form.recipientMode === 'EMAIL_LIST' && (
						<div>
							<label>Email Addresses</label>
							<EmailTagInput
								value={form.recipientEmails}
								onChange={(next) => setForm({ ...form, recipientEmails: next })}
							/>
						</div>
					)}
					<label className='flex items-center gap-2 font-normal'>
						<input
							type='checkbox'
							className='size-4 rounded border-gray-300 accent-gray-400 cursor-pointer'
							checked={form.enabled}
							onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
						/>
						Enabled
					</label>
					<div className='flex gap-3 pt-2'>
						<button type='submit' disabled={saving} className='btn-primary'>
							{saving ? 'Saving...' : 'Save'}
						</button>
						<button type='button' className='btn-secondary' onClick={() => setOpen(false)}>
							Cancel
						</button>
					</div>
				</form>
			</Modal>
		</div>
	);
}
